// The detail panel: one task, its subitems, the conversation on it, and where
// it came from.
//
// The update thread is the half of the monday.com board that carried the real
// information. 301 of them came across in the import, 128 written by one
// person. A board that keeps the titles and loses the conversation is a list,
// not a record, so the thread is the largest thing in this panel rather than a
// tab behind it.
import { useCallback, useEffect, useState } from 'react'
import { CornerDownRight, ExternalLink, Loader2, Plus, Send, X } from 'lucide-react'
import { toast } from 'sonner'
import {
  useAddTaskUpdate,
  useCreateTask,
  useGetTaskDetail,
  useSetTaskLinks,
  useUpdateTask,
} from '../../hooks/backend/tasks'
import { TargetDateRequest } from './TargetDateRequest'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Textarea } from '../../lib/shadcn/textarea'
import { Spinner } from '../../lib/shadcn/spinner'
import { cn } from '../../lib/shadcn/utils'
import {
  AUTHORITY_HINT,
  bubbleClass,
  daysDeltaLabel,
  delegationGapText,
  daysDeltaShort,
  initials,
  linksOf,
  longDateTime,
  onTimeLabel,
  STATUS_CLASS,
  taskRowConfirmed,
  unappliedFields,
  type LinkKind,
  type TaskActivityRow,
  type TaskAsset,
  type TaskLinkOption,
  type TaskPerson,
  type TaskRow,
  type TaskUpdateRow,
  mentionIdsInBody,
  originOf,
} from '../../utils/tasks'
import {
  AuthorityCell,
  DateCell,
  LinkCell,
  NotesCell,
  PeopleCell,
  PriorityCell,
  StatusCell,
  TitleCell,
} from './TaskCells'
import { TaskAttachments } from './TaskAttachments'
import { TaskParentPicker } from './TaskParentPicker'
import { MentionTextarea } from './MentionTextarea'
import { runConfirmed } from '../../hooks/confirmedWrites'

// parent_ref, parent_title and parent_status are joined onto the task row by
// getTaskDetail. They are optional so this panel still opens if it is ever
// served by a Worker built before migration 0119.
type TaskRowWithParent = TaskRow & {
  parent_ref?: number | null
  parent_title?: string | null
  parent_status?: string | null
}

type Detail = {
  task: TaskRowWithParent
  subitems: TaskRow[]
  updates: TaskUpdateRow[]
  activity: TaskActivityRow[]
  // Arrives with db/migrations/0084. Optional so this panel still opens if it is
  // ever served by a Worker built before the attachment handlers existed.
  assets?: TaskAsset[]
}

// One line of history.
//
// Two of the fields on this trail are not field changes, and rendering them in
// the generic shape produces nonsense an operator has to decode: "changed
// attachment from nothing to claim.pdf". The removal line matters more, because
// it is the only place the written reason is ever shown, and a reason nobody can
// read is a reason nobody gave.
function activityLine(a: TaskActivityRow): string {
  if (a.field === 'attachment') {
    return `added the file ${a.new_value ?? 'unknown'}`
  }
  if (a.field === 'attachment_removed') {
    return `removed the file ${a.old_value ?? 'unknown'}, because: ${a.new_value ?? 'no reason was given'}`
  }
  return `changed ${a.field} from ${a.old_value ?? 'nothing'} to ${a.new_value ?? 'nothing'}`
}

// `plain` is for the three fields that are still read-only text and want to be
// cut off rather than wrap. Everything else in this grid now holds a control,
// and truncate on a control clips the select arrow off the right of the pill.
function Field({
  label,
  children,
  plain,
  hint,
}: {
  label: string
  children: React.ReactNode
  plain?: boolean
  hint?: string
}) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground" title={hint}>
        {label}
      </div>
      <div className={cn('mt-0.5 min-w-0 text-sm text-foreground', plain && 'truncate')}>
        {children}
      </div>
    </div>
  )
}

function UpdateBubble({ update, replies }: { update: TaskUpdateRow; replies: TaskUpdateRow[] }) {
  return (
    <div className="rounded-lg border border-border bg-background p-3">
      <div className="flex items-center gap-2">
        <span
          className={cn(
            'flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold text-white',
            bubbleClass(update.author_name),
          )}
        >
          {initials(update.author_name)}
        </span>
        <span className="text-xs font-medium">{update.author_name}</span>
        <span className="ml-auto text-[11px] text-muted-foreground">
          {longDateTime(update.created_at)}
        </span>
      </div>
      <p className="mt-2 whitespace-pre-wrap break-words text-sm text-foreground">{update.body}</p>
      {(update.mentions ?? []).length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Tagged</span>
          {(update.mentions ?? []).map((m) => (
            <span
              key={m.person_id}
              className={cn(
                'rounded px-1.5 py-0.5 text-[11px]',
                m.notified
                  ? 'bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-200'
                  : 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200',
              )}
              // The distinction is not decorative. A mention of somebody with no
              // address on file is a real mention that nobody was told about,
              // and the author needs to know that rather than assume delivery.
              title={
                m.notified
                  ? 'An email was queued to this person.'
                  : 'No email address on file, so nobody was notified. Tell them another way.'
              }
            >
              {m.name}
              {!m.notified && ' (not emailed)'}
            </span>
          ))}
        </div>
      )}
      {replies.length > 0 && (
        <div className="mt-2 space-y-2 border-l-2 border-border pl-3">
          {replies.map((r) => (
            <div key={r.id}>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium">{r.author_name}</span>
                <span className="text-[11px] text-muted-foreground">
                  {longDateTime(r.created_at)}
                </span>
              </div>
              <p className="whitespace-pre-wrap break-words text-sm">{r.body}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function TaskPanel({
  taskId,
  canEdit,
  people,
  linkOptions,
  onClose,
  onChanged,
}: {
  taskId: string
  canEdit: boolean
  people: TaskPerson[]
  linkOptions: TaskLinkOption[]
  onClose: () => void
  onChanged: () => void
}) {
  const detail = useGetTaskDetail()
  const addUpdate = useAddTaskUpdate()
  const createTask = useCreateTask()
  const updateTask = useUpdateTask()
  const setLinks = useSetTaskLinks()
  const [body, setBody] = useState('')
  const [newSub, setNewSub] = useState('')
  const [addingSub, setAddingSub] = useState(false)

  const trigger = detail.trigger
  const load = useCallback(() => {
    void trigger({ task_id: taskId }, { skipCache: true })
  }, [trigger, taskId])

  useEffect(load, [load])

  const data = detail.data as Detail | null

  // The panel saves through the same two endpoints the board rows use, and
  // reloads both itself and the board afterwards. The error is surfaced
  // verbatim: a field that silently reverts is how somebody walks away
  // believing a change landed.
  const save = async (patch: Record<string, unknown>) => {
    // Same terms as the board: nothing moves until the server confirms, and
    // one declaration covers both, so a status changed here and the card behind
    // the panel land together. See taskRowConfirmed in utils/tasks.ts.
    await runConfirmed(
      () => updateTask.trigger({ task_id: taskId, patch }),
      (confirmed) => {
        const row = (confirmed ?? null) as Record<string, unknown> | null
        const missed = unappliedFields(patch, row)
        if (missed.length > 0) {
          toast.error('That change was accepted but not saved', {
            description: `The server returned this task with ${missed.join(' and ')} unchanged. Nothing was written. Please report this rather than trying again.`,
          })
        }
        return taskRowConfirmed(taskId, row)
      },
    )
    load()
    onChanged()
  }

  const saveLinks = async (kind: LinkKind, ids: string[]) => {
    try {
      await setLinks.trigger({ task_id: taskId, kind, ids })
      load()
      onChanged()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'The link was not saved.')
    }
  }

  const post = async () => {
    if (!body.trim()) return
    try {
      // Derived from the body, not from what the picker remembers being clicked.
      // Delete the words and the mention goes with them; see mentionIdsInBody.
      await addUpdate.trigger({
        task_id: taskId,
        body,
        mention_person_ids: mentionIdsInBody(body, people),
      })
      setBody('')
      load()
      onChanged()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'The update was not posted.')
    }
  }

  // Moving this task under another, and back out again. Both go through the
  // same patch key: a parent makes it a subtask, null promotes it. Migration
  // 0119 owns every refusal and writes them for the person reading the screen,
  // so the message is shown exactly as the database phrased it rather than
  // replaced with a generic one that loses which task was in the way.
  const moveUnder = async (parentId: string | null) => {
    try {
      await save({ parent_task_id: parentId })
      toast.success(parentId ? 'Moved.' : 'Promoted to a task of its own.')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'The task was not moved.')
    }
  }

  const addSubitem = async () => {
    if (!newSub.trim()) return
    try {
      await createTask.trigger({ title: newSub, parent_task_id: taskId })
      setNewSub('')
      setAddingSub(false)
      load()
      onChanged()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'The subtask was not added.')
    }
  }

  if (detail.loading && !data) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Opening…
      </div>
    )
  }

  if (detail.error) {
    return (
      <div className="p-4">
        <p className="text-sm text-rose-600 dark:text-rose-400">{detail.error}</p>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        That task could not be opened. It may have been removed while this screen was open.
      </div>
    )
  }

  const t = data.task
  const ot = onTimeLabel(t.on_time)
  const roots = data.updates.filter((u) => !u.parent_update_id)
  const repliesOf = (id: string) => data.updates.filter((u) => u.parent_update_id === id)

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-start gap-2 border-b border-border px-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[11px] tabular-nums text-muted-foreground">#{t.ref}</span>
            {/* This was a read-only pill. It is the same pill, and now changing
                it changes the task, through the same endpoint and the same
                confirmed-write path the board row uses. The status was the one
                field somebody would open a task to change and then have to
                close it again to do. */}
            <div className="w-32">
              <StatusCell
                value={t.status}
                group={t.board_group}
                save={save}
                disabled={!canEdit}
              />
            </div>
          </div>
          {/* Renamed where it is read. The board row does the same thing, and
              both go through updateTask so the activity trail records who. */}
          <div className="mt-1 flex text-base font-semibold leading-snug">
            <TitleCell
              value={t.title}
              save={save}
              disabled={!canEdit}
              className="whitespace-pre-wrap break-words"
            />
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="grid grid-cols-2 gap-3 border-b border-border p-4 sm:grid-cols-3">
          <Field label="Owner">
            <div className="flex items-center gap-2">
              <PeopleCell
                names={t.assignee_names}
                unlinked={t.assignee_unlinked}
                people={people}
                save={save}
                disabled={!canEdit}
              />
              <span className="truncate text-xs text-muted-foreground">
                {t.assignee_names.length > 0 ? t.assignee_names.join(', ') : 'Unassigned'}
              </span>
            </div>
          </Field>
          <Field label="Priority">
            <PriorityCell value={t.priority} save={save} disabled={!canEdit} />
          </Field>
          {/* Hassan Osman's fourth step, and the one this board has never held.
              The hint is the whole point of putting it here as well as on the
              row: the failure this field prevents is two people assuming
              different answers, so the words that distinguish the three levels
              have to be readable at the moment somebody sets it. */}
          <Field
            label="Authority"
            hint={
              t.authority_level
                ? AUTHORITY_HINT[t.authority_level]
                : 'Nobody has said how much authority the owner has. Recommend, Inform and initiate, or Act.'
            }
          >
            <AuthorityCell value={t.authority_level} save={save} disabled={!canEdit} />
          </Field>
          {/* Group stays read-only, and it is the one field here that should.
              The database decides the group from the status, except for
              Standing; see task_update_fields_v1 and db/migrations/0023. A
              second control that set it directly would let somebody create the
              group-and-status conflict this board already carries 18 of. */}
          <Field
            label="Group"
            plain
            hint="The group follows the status. Change the status to move this card."
          >
            {t.board_group}
          </Field>
          <Field label="Start">
            <DateCell value={t.start_date} field="start_date" save={save} disabled={!canEdit} />
          </Field>
          {/* The picker writes the FIRST target date directly, which needs no
              approval either way. Once one is set, the request control below is
              the intended path, and when the gate in sin_offering_policy is on,
              task_update_fields_v1 refuses the direct edit and says so.
              Both live in the same Field so the route is read where the date is,
              rather than being discovered as an error message.

              THE HINT DOES NOT NAME THE GATE, deliberately. This panel does not
              read sin_offering_policy and would have to be told, and a hint that
              promises "needs an approval" while the gate is off is worse than no
              hint: it describes a rule the board is not enforcing. What is true
              in both states is that a change goes through the request, so that is
              what it says. */}
          <Field
            label="Target"
            hint={
              t.target_completion
                ? 'Changing a date already set is a request, so the reason is on the record.'
                : 'The first target date needs no approval.'
            }
          >
            <DateCell
              value={t.target_completion}
              field="target_completion"
              save={save}
              disabled={!canEdit}
              tone={t.is_overdue ? 'text-rose-600 dark:text-rose-400 font-semibold' : undefined}
            />
            {canEdit && (
              <TargetDateRequest
                taskId={t.id}
                currentDate={t.target_completion}
                onRequested={() => {
                  load()
                  onChanged()
                }}
              />
            )}
          </Field>
          {/* Editable, and worth saying why: completing a task stamps this date
              by itself, so most of the time nobody touches it. It is here for
              the case the stamp gets wrong, which is the task that was actually
              finished last Thursday and only marked Done today. */}
          <Field label="Completed">
            <DateCell
              value={t.actual_completion}
              field="actual_completion"
              save={save}
              disabled={!canEdit}
            />
          </Field>
          <Field label="On time" plain>
            <span className={ot.tone}>
              {ot.text}
              {t.days_delta !== null && (
                <span className="ml-1 text-xs opacity-70" title={daysDeltaLabel(t.days_delta)}>
                  {daysDeltaShort(t.days_delta)}
                </span>
              )}
            </span>
          </Field>
          <Field label="Next update">
            <DateCell value={t.next_update} field="next_update" save={save} disabled={!canEdit} />
          </Field>
          <Field label="Source" plain>
            {t.source_meeting ?? '—'}
          </Field>
        </div>

        {/* WHAT IS MISSING FROM THIS DELEGATION, said out loud.
            v_task_board.delegation_gaps, added 27 August 2026. It is empty on a
            finished task by the view's own choice, so this strip never appears on
            an archive row.

            It is a statement and not a warning colour on purpose. 359 of the
            tasks on this board are missing at least one of these four today, and
            a red banner on every row would be ignored inside a week. The words
            are enough: they name the thing to fix and they can be fixed from the
            two fields directly above. */}
        {delegationGapText(t.delegation_gaps) && (
          <div className="border-b border-border bg-muted/50 px-4 py-2">
            <p className="text-xs text-muted-foreground">
              {delegationGapText(t.delegation_gaps)}{' '}
              Hassan Osman's five steps are the task, the goal, the timeline, the authority level
              and the written record. This one is not finished being handed over.
            </p>
          </div>
        )}

        {/* WHAT THIS TASK IS ABOUT.
            Every task on this board concerns a car, the person who owns it or
            the person driving it, and until now that lived inside the title as
            free text: "Submit insurer claim file for Bokamoso's LY80CBGP".
            Unsearchable in the one direction that matters, which is the
            direction you need when the owner is on the phone. These are real
            foreign keys; see db/migrations/0025. */}
        <div className="grid grid-cols-1 gap-3 border-b border-border p-4 sm:grid-cols-3">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Vehicles
            </div>
            <div className="mt-1">
              <LinkCell
                kinds={['vehicle']}
                links={{ vehicle: linksOf(t, 'vehicle') }}
                options={linkOptions}
                save={saveLinks}
                disabled={!canEdit}
                title="Vehicles"
              />
            </div>
          </div>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Vehicle owners
            </div>
            <div className="mt-1">
              <LinkCell
                kinds={['owner']}
                links={{ owner: linksOf(t, 'owner') }}
                options={linkOptions}
                save={saveLinks}
                disabled={!canEdit}
                title="Vehicle owners"
              />
            </div>
          </div>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Drivers</div>
            <div className="mt-1">
              <LinkCell
                kinds={['driver']}
                links={{ driver: linksOf(t, 'driver') }}
                options={linkOptions}
                save={saveLinks}
                disabled={!canEdit}
                title="Drivers"
              />
            </div>
          </div>
        </div>

        {/* Shown even when empty, and editable. It used to render only when
            there was already something in it, which meant the only way to add
            the first note was to have had one. */}
        {(t.description || canEdit) && (
          <div className="border-b border-border p-4">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Notes</div>
            <div className="mt-1">
              <NotesCell
                value={t.description}
                field="description"
                save={save}
                disabled={!canEdit}
                placeholder="Nothing written here yet. Click to add what this is about."
              />
            </div>
          </div>
        )}

        <TaskAttachments
          taskId={taskId}
          assets={data.assets ?? []}
          canEdit={canEdit}
          onChanged={() => {
            load()
            onChanged()
          }}
        />

        {/* Raised by another board. The link is the point: a CCMA subtask, a
            content card and a dev plan item all describe something that lives
            somewhere else, and a task that cannot be clicked through to its
            subject makes the reader go and find it. Arrives with
            db/migrations/0232. */}
        {(() => {
          const o = originOf(t)
          if (!o) return null
          return (
            <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
              <span className={'rounded px-1.5 py-0.5 text-[10px] font-medium ' + o.tone}>
                {o.label}
              </span>
              {o.url ? (
                <a
                  href={o.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-[12px] underline underline-offset-2"
                >
                  Open it where it lives
                  <ExternalLink className="h-3 w-3" />
                </a>
              ) : (
                <span className="text-[12px] text-muted-foreground">
                  No link was recorded for this one.
                </span>
              )}
            </div>
          )
        })()}

        {/* Provenance. A task nobody typed has to be able to show where it came
            from, verbatim, or the automation is asking to be trusted rather
            than checked. */}
        {t.source_excerpt && (
          <div className="border-b border-border bg-violet-50 p-4 dark:bg-violet-950/30">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-medium uppercase tracking-wide text-violet-800 dark:text-violet-300">
                Created from meeting notes
              </span>
              {t.source_doc_url && (
                <a
                  href={t.source_doc_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-[11px] text-violet-800 underline dark:text-violet-300"
                >
                  Open the notes
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
            <p className="mt-1 whitespace-pre-wrap break-words text-sm italic text-violet-900 dark:text-violet-200">
              {t.source_excerpt}
            </p>
          </div>
        )}

        {/* Where this task sits. Subtasks have existed since the board shipped
            and could only ever be set at creation; migration 0119 made the
            parent a field like any other. This block is the whole of that
            feature on screen, and it is here rather than on the board row
            because the board row's file is edited by every task change and this
            is the second control, not the first. */}
        <div className="border-b border-border p-4">
          <div className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Part of
            </span>
          </div>
          {t.parent_task_id ? (
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
              <CornerDownRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate">
                <span className="tabular-nums text-muted-foreground">#{t.parent_ref ?? '—'}</span>{' '}
                {t.parent_title ?? 'a task that could not be read'}
              </span>
              {canEdit && (
                <>
                  <TaskParentPicker
                    taskId={taskId}
                    currentParentId={t.parent_task_id}
                    onChoose={(id) => moveUnder(id)}
                    align="end"
                  >
                    <button type="button" className="text-xs text-muted-foreground hover:text-foreground">
                      Change
                    </button>
                  </TaskParentPicker>
                  <button
                    type="button"
                    onClick={() => void moveUnder(null)}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Promote to task
                  </button>
                </>
              )}
            </div>
          ) : t.subitem_count > 0 ? (
            /* Said out loud rather than shown as a missing button. A control
               that is simply absent sends somebody looking for a fault; this
               names the rule and what to do about it. */
            <p className="mt-1 text-sm text-muted-foreground">
              Nothing. This task has {t.subitem_count} subtask{t.subitem_count === 1 ? '' : 's'} of
              its own, and subtasks only go one level deep, so it cannot become one until those are
              moved out.
            </p>
          ) : (
            <div className="mt-1 flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Nothing. This is a task in its own right.</span>
              {canEdit && (
                <TaskParentPicker
                  taskId={taskId}
                  currentParentId={null}
                  onChoose={(id) => moveUnder(id)}
                  align="end"
                >
                  <button type="button" className="ml-auto text-xs text-muted-foreground hover:text-foreground">
                    Make it a subtask
                  </button>
                </TaskParentPicker>
              )}
            </div>
          )}
        </div>

        {/* Subtasks. Called subitems by monday.com and by the view columns; the
            screen uses the plainer word, which is what people actually asked
            for when they could not find this feature. */}
        <div className="border-b border-border p-4">
          <div className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Subtasks
            </span>
            <span className="text-[11px] tabular-nums text-muted-foreground">
              {t.subitem_done_count}/{t.subitem_count}
            </span>
            {canEdit && !t.parent_task_id && (
              <button
                type="button"
                onClick={() => setAddingSub(true)}
                className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              >
                <Plus className="h-3.5 w-3.5" /> Add
              </button>
            )}
          </div>
          {data.subitems.length === 0 && !addingSub && (
            <p className="mt-1 text-sm text-muted-foreground">None.</p>
          )}
          <ul className="mt-2 space-y-1">
            {data.subitems.map((s) => (
              <li key={s.id} className="flex items-center gap-2 text-sm">
                <span
                  className={cn(
                    'rounded px-1.5 py-0.5 text-[10px] font-semibold',
                    STATUS_CLASS[s.status],
                  )}
                >
                  {s.status}
                </span>
                <span className={cn('min-w-0 flex-1 truncate', s.status === 'Done' && 'line-through text-muted-foreground')}>
                  {s.title}
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {s.assignee_names.join(', ') || '—'}
                </span>
              </li>
            ))}
          </ul>
          {addingSub && (
            <div className="mt-2 flex gap-2">
              <Input
                autoFocus
                value={newSub}
                onChange={(e) => setNewSub(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void addSubitem()
                  if (e.key === 'Escape') setAddingSub(false)
                }}
                placeholder="What is the subtask?"
                className="h-8 text-sm"
              />
              <Button size="sm" className="h-8" onClick={() => void addSubitem()}>
                Add
              </Button>
            </div>
          )}
        </div>

        {/* The conversation */}
        <div className="p-4">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Updates ({data.updates.length})
          </div>
          {canEdit && (
            <div className="mt-2">
              <MentionTextarea
                value={body}
                onChange={setBody}
                people={people}
                placeholder="Write an update. Type @ to tag somebody. Everyone on the board can read it."
              />
              <div className="mt-2 flex justify-end">
                <Button
                  size="sm"
                  disabled={!body.trim() || addUpdate.loading}
                  onClick={() => void post()}
                  className="gap-1.5"
                >
                  {addUpdate.loading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Send className="h-3.5 w-3.5" />
                  )}
                  Post
                </Button>
              </div>
            </div>
          )}
          <div className="mt-3 space-y-3">
            {roots.length === 0 && (
              <p className="text-sm text-muted-foreground">Nothing has been said on this one yet.</p>
            )}
            {roots
              .slice()
              .reverse()
              .map((u) => (
                <UpdateBubble key={u.id} update={u} replies={repliesOf(u.id)} />
              ))}
          </div>
        </div>

        {data.activity.length > 0 && (
          <div className="border-t border-border p-4">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">History</div>
            <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
              {data.activity.map((a, i) => (
                <li key={i} className="flex flex-wrap gap-1">
                  <span className="tabular-nums">{longDateTime(a.at)}</span>
                  <span className="font-medium text-foreground">{a.actor_email ?? 'system'}</span>
                  <span>{activityLine(a)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}
